// Determine API URL based on environment
// Use Next.js API proxy for same-origin requests (fixes cookie issues)
const getApiUrl = (): string => {
  // In browser, use relative URL to go through Next.js API proxy
  if (typeof window !== 'undefined') {
    return ''; // Empty string means same origin - will use Next.js API proxy
  }
  
  // On server side, use direct backend URL
  if (process.env.NEXT_PUBLIC_API_URL) {
    return process.env.NEXT_PUBLIC_API_URL;
  }
  
  return 'http://localhost:3006';
};

export const API_URL = getApiUrl();

export interface User {
  uid: number;
  name: string;
  mail: string;
}

export interface LoginResponse {
  success: true;
  user: User;
}

export interface AuthCheckResponse {
  authenticated: boolean;
  user: User | null;
}

export interface LogoutResponse {
  success: true;
  message: string;
}

/**
 * Login with username and password
 */
export async function login(username: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${API_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Important: include cookies
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Login failed');
  }

  return response.json();
}

/**
 * Logout
 */
export async function logout(): Promise<LogoutResponse> {
  const response = await fetch(`${API_URL}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include', // Important: include cookies
  });

  if (!response.ok) {
    throw new Error('Logout failed');
  }

  return response.json();
}

/**
 * Get current user
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      credentials: 'include', // Important: include cookies
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.user || null;
  } catch (error) {
    return null;
  }
}

/**
 * Check authentication status
 */
export async function checkAuth(): Promise<AuthCheckResponse> {
  try {
    const response = await fetch(`${API_URL}/api/auth/check`, {
      credentials: 'include', // Important: include cookies
    });

    if (!response.ok) {
      return { authenticated: false, user: null };
    }

    return response.json();
  } catch (error) {
    return { authenticated: false, user: null };
  }
}
